import { useState } from 'react';
import { Star, Trash2, MoreVertical, Check, X } from 'lucide-react';
import type { AssetRecord } from '../../../types';
import { useAssetStore } from '../../../stores/assetStore';
import { formatBytes } from '../../../lib/assetCompression';

interface Props {
  asset: AssetRecord;
  onInsert: (asset: AssetRecord) => void;
}

export const ASSET_DRAG_MIME = 'application/x-cs-asset';

export default function AssetTile({ asset, onInsert }: Props) {
  const { canDelete, folders, toggleFavorite, deleteAsset, renameAsset, moveAssetToFolder } =
    useAssetStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(asset.name);
  const [moveMenuOpen, setMoveMenuOpen] = useState(false);

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData(ASSET_DRAG_MIME, asset.id);
    e.dataTransfer.setData('text/plain', asset.name);
    if (asset.thumbnail_url) {
      const img = new Image();
      img.src = asset.thumbnail_url;
      try {
        e.dataTransfer.setDragImage(img, 20, 20);
      } catch {
        // ignore
      }
    }
  };

  const handleRename = async () => {
    const trimmed = newName.trim();
    if (trimmed && trimmed !== asset.name) {
      await renameAsset(asset.id, trimmed);
    }
    setRenaming(false);
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDoubleClick={() => onInsert(asset)}
      className="group relative rounded-lg overflow-hidden border border-white/[0.06] bg-white/[0.02] hover:border-cyan-500/30 transition-all cursor-grab active:cursor-grabbing"
      title={`Drag to canvas or double-click to insert - ${asset.name}`}
    >
      <div className="aspect-square bg-[#0a0a0a] flex items-center justify-center relative">
        {asset.thumbnail_url ? (
          <img
            src={asset.thumbnail_url}
            alt={asset.name}
            className="w-full h-full object-contain pointer-events-none"
            loading="lazy"
            draggable={false}
          />
        ) : (
          <div className="text-zinc-700 text-[10px]">No preview</div>
        )}

        <div className="absolute top-1 left-1 flex items-center gap-1">
          <span className="px-1.5 py-0.5 rounded bg-black/60 text-[8px] text-zinc-300 uppercase font-medium tracking-wider">
            {asset.asset_type}
          </span>
          {asset.compressed && (
            <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 text-[8px] uppercase tracking-wider" title="Compressed">
              opt
            </span>
          )}
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleFavorite(asset.id);
          }}
          className={`absolute top-1 right-1 p-1 rounded-full transition-all ${
            asset.is_favorite
              ? 'bg-amber-500/90 text-white opacity-100'
              : 'bg-black/60 text-zinc-400 opacity-0 group-hover:opacity-100 hover:text-amber-300'
          }`}
          title={asset.is_favorite ? 'Unfavorite' : 'Favorite'}
        >
          <Star size={10} className={asset.is_favorite ? 'fill-white' : ''} />
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
          }}
          className="absolute bottom-1 right-1 p-1 rounded-full bg-black/60 text-zinc-300 opacity-0 group-hover:opacity-100 hover:bg-black/80 transition-all"
        >
          <MoreVertical size={10} />
        </button>
      </div>

      <div className="p-1.5">
        {renaming ? (
          <div className="flex items-center gap-1">
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onBlur={handleRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRename();
                if (e.key === 'Escape') {
                  setRenaming(false);
                  setNewName(asset.name);
                }
              }}
              className="flex-1 px-1 py-0.5 bg-white/[0.06] border border-cyan-500/30 rounded text-[10px] text-zinc-200 focus:outline-none min-w-0"
            />
          </div>
        ) : (
          <>
            <p className="text-[10px] text-zinc-300 truncate font-medium">{asset.name}</p>
            <div className="flex items-center justify-between text-[9px] text-zinc-600">
              <span>
                {asset.width && asset.height ? `${asset.width}x${asset.height}` : formatBytes(asset.file_size)}
              </span>
              {asset.usage_count > 0 && <span>x{asset.usage_count}</span>}
            </div>
          </>
        )}
      </div>

      {menuOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
          <div className="absolute bottom-8 right-1 z-20 w-36 bg-[#141414] border border-white/[0.1] rounded-lg shadow-2xl py-1 text-[10px]">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onInsert(asset);
                setMenuOpen(false);
              }}
              className="w-full text-left px-3 py-1.5 text-zinc-300 hover:bg-white/[0.06]"
            >
              Insert on canvas
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setRenaming(true);
                setMenuOpen(false);
              }}
              className="w-full text-left px-3 py-1.5 text-zinc-300 hover:bg-white/[0.06]"
            >
              Rename
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setMoveMenuOpen(true);
                setMenuOpen(false);
              }}
              className="w-full text-left px-3 py-1.5 text-zinc-300 hover:bg-white/[0.06]"
            >
              Move to folder
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (asset.thumbnail_url) {
                  navigator.clipboard?.writeText(asset.thumbnail_url);
                }
                setMenuOpen(false);
              }}
              className="w-full text-left px-3 py-1.5 text-zinc-300 hover:bg-white/[0.06]"
            >
              Copy URL
            </button>
            {canDelete && (
              <>
                <div className="border-t border-white/[0.06] my-1" />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Delete "${asset.name}"? This cannot be undone.`)) {
                      deleteAsset(asset.id);
                    }
                    setMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-1.5 text-red-400 hover:bg-red-500/10 flex items-center gap-1.5"
                >
                  <Trash2 size={10} />
                  Delete
                </button>
              </>
            )}
          </div>
        </>
      )}

      {moveMenuOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setMoveMenuOpen(false)} />
          <div className="absolute bottom-8 right-1 z-20 w-36 max-h-52 overflow-y-auto bg-[#141414] border border-white/[0.1] rounded-lg shadow-2xl py-1 text-[10px]">
            <button
              onClick={(e) => {
                e.stopPropagation();
                moveAssetToFolder(asset.id, null);
                setMoveMenuOpen(false);
              }}
              className="w-full text-left px-3 py-1.5 text-zinc-400 hover:bg-white/[0.06] flex items-center justify-between"
            >
              <span>No folder</span>
              {!asset.folder_id && <Check size={10} className="text-cyan-400" />}
            </button>
            <div className="border-t border-white/[0.06] my-1" />
            {folders.map((f) => (
              <button
                key={f.id}
                onClick={(e) => {
                  e.stopPropagation();
                  moveAssetToFolder(asset.id, f.id);
                  setMoveMenuOpen(false);
                }}
                className="w-full text-left px-3 py-1.5 text-zinc-300 hover:bg-white/[0.06] flex items-center justify-between"
              >
                <span className="truncate">{f.name}</span>
                {asset.folder_id === f.id && <Check size={10} className="text-cyan-400" />}
              </button>
            ))}
            <div className="border-t border-white/[0.06] my-1" />
            <button
              onClick={(e) => {
                e.stopPropagation();
                setMoveMenuOpen(false);
              }}
              className="w-full text-left px-3 py-1.5 text-zinc-500 hover:bg-white/[0.06] flex items-center gap-1.5"
            >
              <X size={10} />
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}
